import { useMemo, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useListingUpdateTagsMutation,
  useListingAddNoteMutation,
  useListingDeleteNoteMutation,
  useListingDetailsQuery,
  useToggleContactIsDisabledMutation
} from "./useListing";
import { ContactInfo, EnumProductTypes, Listing } from "src/types/listing";
import { Note } from "src/types/listing";

const useListingDetails = ({
  listingId,
  onNoteAdded,
  onNoteDeleted,
  onTagsSaved,
  productType,
  isHideDNCPhoneNumbers = false,
}: {
  listingId: string;
  onNoteAdded?: (updatedNotes: Note[]) => void;
  onNoteDeleted?: (updatedNotes: Note[]) => void;
  onTagsSaved?: (updatedTags: string[]) => void;
  productType: EnumProductTypes;
  isHideDNCPhoneNumbers?: boolean;
}) => {
  const queryClient = useQueryClient();
  const queryKey = useMemo(() => ["listingDetails", listingId], [listingId]);
  const {
    data: listing,
    isError,
    isLoading,
  } = useListingDetailsQuery(productType, listingId, { isHideDNCPhoneNumbers });
  const { mutateAsync: addNote } = useListingAddNoteMutation({ productType });
  const { mutateAsync: deleteNote } = useListingDeleteNoteMutation({
    productType,
  });
  const { mutateAsync: saveTags } = useListingUpdateTagsMutation({
    productType,
  });

  const { mutateAsync: toggleContactIsDisabled } =
  useToggleContactIsDisabledMutation({ productType });

  const optimisticUpdateListing = useCallback(
    (updatedListing: Listing) => {
      const prevListing = queryClient.getQueryData(queryKey);
      queryClient.setQueryData(queryKey, updatedListing);
      return { prevListing, updatedListing };
    },
    [queryClient, queryKey]
  );

  const handleTagsSaveClick = useCallback(
    async (updatedTags: string[]) => {
      try {
        await saveTags({ listingId, tags: updatedTags });
        onTagsSaved && onTagsSaved(updatedTags);
      } catch (error) {
        console.error("Error saving tags:", error);
      }
    },
    [listingId, onTagsSaved, saveTags]
  );

  const handleAddNoteClick = useCallback(
    async (noteText: string) => {
      if (!noteText || noteText.length === 0) {
        return;
      }
      try {
        const savedNote = await addNote({
          listingId,
          note: noteText,
        });
        onNoteAdded && onNoteAdded([savedNote, ...(listing?.notes || [])]);
      } catch (error) {
        console.error("Error adding note:", error);
      }
    },
    [listingId, addNote, listing, onNoteAdded]
  );

  const handleDeleteNoteClick = useCallback(
    async (noteId: string) => {
      try {
        await deleteNote({
          noteId,
          listingId,
        });
        onNoteDeleted &&
          onNoteDeleted(
            (listing?.notes || []).filter((item) => item.id !== noteId)
          );
      } catch (error) {
        console.error("Error deleting note:", error);
      }
    },
    [onNoteDeleted, deleteNote, listing, listingId]
  );

  const handleToggleContactIsDisabled = useCallback(
    async (contact: ContactInfo, isDisabled: boolean) => {
      try {
        await toggleContactIsDisabled({
          phone: contact.phone,
          listingId: listing?.id as string,
          propertyId: listing?.propertyId as string,
          isDisabled: isDisabled === undefined ? !contact.hidden : isDisabled,
        });
      } catch (error) {
        console.error("Error toggling contact is disabled:", error);
      }
    },
    [toggleContactIsDisabled, listing]
  );


  return {
    listing,
    isError,
    isLoading,
    optimisticUpdateListing,
    handleTagsSaveClick,
    handleAddNoteClick,
    handleDeleteNoteClick,
    handleToggleContactIsDisabled
  };
};

export default useListingDetails;
